import React, { useState, useEffect } from "react";
import { Box, InputAdornment } from "@mui/material";
import { C, NAV, fmtINR } from "./ipd-billing/constants";
import SvgIcon from "./ipd-billing/SvgIcon";
import IPDBillingLayout from "./ipd-billing/components/IPDBillingLayout";
import IpdButton from "./ipd/utils/IpdButton";
import IpdTextField from "./ipd/utils/IpdTextField";
import IpdDataGrid from "./ipd/utils/IpdDataGrid";
import {
  getBillingDashboardSummary,
  getHighOutstandingAccounts,
} from "./ipd-billing/billing-api";

// ─────────────────────────────────────────────────────────────────────────────
// Revenue-by-Department has no backend endpoint yet, so it stays on sample data.
// KPIs, the weekly collections chart and the high-outstanding table are all live
// (GET /ipd/billing/dashboard/summary/ + /dashboard/high-outstanding/).
// ─────────────────────────────────────────────────────────────────────────────
const MOCK_DEPT_REVENUE = [
  { name: "Cardiology",      amount: 4820000, pct: 100  },
  { name: "Orthopedics",     amount: 3650000, pct: 75.7 },
  { name: "Oncology",        amount: 3120000, pct: 64.7 },
  { name: "Neurology",       amount: 2480000, pct: 51.5 },
  { name: "General Surgery", amount: 2150000, pct: 44.6 },
  { name: "Pediatrics",      amount: 1380000, pct: 28.6 },
  { name: "Others",          amount: 3060000, pct: 63.5 },
];

// ── API → view mappers ─────────────────────────────────────────────────────────
function fmtShort(value) {
  const n = Number(value) || 0;
  const abs = Math.abs(n);
  if (abs >= 1e7) return `₹${(n / 1e7).toFixed(2)}Cr`;
  if (abs >= 1e5) return `₹${(n / 1e5).toFixed(1)}L`;
  return fmtINR(n);
}

function initialsOf(name) {
  const parts = String(name || "").trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "—";
  return (parts[0][0] + (parts[1]?.[0] || "")).toUpperCase();
}

function buildKpis(s) {
  const wow = s.daily_collections_wow_percent;
  const wowNum = wow == null ? null : Number(wow);
  return [
    { id: "admitted",    title: "Admitted Patients",           value: String(s.admitted_patients ?? 0),  badge: "",                                                                            sub: "currently admitted", icon: "bed",     iconColor: "#0d233f", iconBg: "#E9F5FE" },
    { id: "discharge",   title: "Pending Discharge Bills",     value: String(s.pending_discharge ?? 0),   badge: "",                                                                            sub: "awaiting discharge", icon: "invoice", iconColor: "#e0a800", iconBg: "#fff8e1" },
    { id: "outstanding", title: "Outstanding Dues",            value: fmtShort(s.outstanding_dues_amount),badge: "", badgeType: "danger",                                                       sub: "payment pending",    icon: "rupee",   iconColor: "#dc3545", iconBg: "#fdecea" },
    { id: "insurance",   title: "Insurance / Corp Pending",    value: fmtShort(s.insurance_pending_amount),badge: s.insurance_pending_count ? `${s.insurance_pending_count} accounts` : "", badgeType: "primary", sub: "under approval", icon: "shield", iconColor: "#0d233f", iconBg: "#E9F5FE" },
    { id: "collection",  title: "Daily Collection",            value: fmtShort(s.daily_collection_amount),badge: wowNum == null ? "" : `${wowNum >= 0 ? "↗ +" : "↘ "}${wow}% WoW`, badgeType: wowNum != null && wowNum < 0 ? "danger" : "success", sub: "today", icon: "wallet", iconColor: "#198754", iconBg: "#d1f2db" },
    { id: "refunds",     title: "Refunds Pending",             value: String(s.refunds_pending ?? 0),     badge: Number(s.refunds_pending_amount) > 0 ? fmtShort(s.refunds_pending_amount) : "", badgeType: "warning", sub: "to process", icon: "refresh", iconColor: "#e0a800", iconBg: "#fff8e1" },
  ];
}

const RISK_TITLE = { high: "High", medium: "Medium", low: "Low" };

function mapOutstandingRow(r) {
  const name = r.patient_name || String(r.patient_id || "");
  return {
    id: r.admission_id,
    admission_id: r.admission_id,
    name,
    age: "",
    uhid: String(r.uhid || r.patient_id || ""),
    abha: false,
    initials: initialsOf(name),
    ward: r.ward_bed || "—",
    payer: r.payer || "SELF_PAY",
    los: `${r.los_days ?? 0}d`,
    billed: Number(r.billed_amount) || 0,
    deposit: Number(r.deposit_amount) || 0,
    balance: Number(r.balance_amount) || 0,
    risk: RISK_TITLE[String(r.risk_level || "").toLowerCase()] || "Low",
  };
}

function mapCollections(week) {
  if (!Array.isArray(week)) return [];
  // Chart is labelled "₹ Lakh", so convert rupee totals to lakhs (1 decimal).
  return week.map((d) => ({
    day: d.day,
    amount: Math.round((Number(d.amount) || 0) / 1e4) / 10,
  }));
}

// ─────────────────────────────────────────────────────────────────────────────
// PAGE-SPECIFIC SUB-COMPONENTS
// ─────────────────────────────────────────────────────────────────────────────
const badgeColors = {
  success: { bg: "#d1f2db", color: "#145a32" },
  warning: { bg: "#fff8e1", color: "#856404" },
  danger:  { bg: "#fdecea", color: "#842029" },
  primary: { bg: "#E9F5FE", color: "#0d233f" },
};

function KPICard({ data }) {
  const badge = badgeColors[data.badgeType] || badgeColors.primary;
  return (
    <div
      style={{ background: C.card, borderRadius: "10px", padding: "18px 20px", border: `1px solid ${C.border}`, boxShadow: "0 2px 8px rgba(13,35,63,0.07)", flex: "1 1 calc(16.66% - 14px)", minWidth: "160px", display: "flex", flexDirection: "column", gap: "10px", cursor: "pointer", transition: "box-shadow 0.2s" }}
      onMouseEnter={e => e.currentTarget.style.boxShadow = "0 4px 16px rgba(13,35,63,0.14)"}
      onMouseLeave={e => e.currentTarget.style.boxShadow = "0 2px 8px rgba(13,35,63,0.07)"}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <span style={{ fontSize: "12px", fontWeight: "600", color: C.textSub }}>{data.title}</span>
        <div style={{ width: "34px", height: "34px", borderRadius: "8px", background: data.iconBg, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <SvgIcon name={data.icon} size={17} color={data.iconColor} />
        </div>
      </div>
      <h3 style={{ margin: 0, fontSize: "24px", fontWeight: "700", color: C.text }}>{data.value}</h3>
      <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
        {data.badge && <span style={{ fontSize: "11px", fontWeight: "700", padding: "2px 8px", borderRadius: "100px", background: badge.bg, color: badge.color }}>{data.badge}</span>}
        <span style={{ fontSize: "11px", color: C.textSub }}>{data.sub}</span>
      </div>
    </div>
  );
}

function CollectionChart({ data, wowPercent }) {
  const [hovered, setHovered] = useState(null);
  const max = Math.max(1, ...data.map(d => d.amount));
  const H = 200;
  const wowNum = wowPercent == null ? null : Number(wowPercent);
  return (
    <div style={{ background: C.card, borderRadius: "10px", padding: "20px", border: `1px solid ${C.border}`, flex: "1 1 55%", boxShadow: "0 2px 8px rgba(13,35,63,0.07)" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "18px" }}>
        <div>
          <h3 style={{ margin: "0 0 2px", fontSize: "15px", fontWeight: "700", color: C.text }}>Daily Collections (₹ Lakh)</h3>
          <span style={{ fontSize: "12px", color: C.textSub }}>Weekly performance summary</span>
        </div>
        {wowNum != null && (
          <span style={{ fontSize: "12px", fontWeight: "700", padding: "4px 10px", borderRadius: "100px", ...(wowNum < 0 ? badgeColors.danger : badgeColors.success) }}>
            {wowNum >= 0 ? "↗ +" : "↘ "}{wowPercent}% WoW
          </span>
        )}
      </div>
      {data.length === 0 ? (
        <div style={{ height: `${H}px`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: "13px", color: C.textSub }}>No collections this week yet.</div>
      ) : (
      <div style={{ position: "relative", height: `${H}px` }}>
        {[0, 0.25, 0.5, 0.75, 1].map((r, i) => (
          <div key={i} style={{ position: "absolute", left: 0, right: 0, top: `${(1 - r) * (H - 40)}px`, borderTop: `1px dashed ${C.border}` }} />
        ))}
        {hovered !== null && (
          <div style={{ position: "absolute", left: `${10 + hovered * ((100 - 10) / data.length) + 1}%`, top: `${(1 - data[hovered].amount / max) * (H - 50) - 28}px`, transform: "translateX(-40%)", background: C.primary, color: "#fff", fontSize: "11px", fontWeight: "700", padding: "4px 8px", borderRadius: "4px", pointerEvents: "none", whiteSpace: "nowrap", zIndex: 10 }}>
            ₹{data[hovered].amount}L
          </div>
        )}
        <svg width="100%" height="100%" style={{ overflow: "visible" }}>
          <defs>
            <linearGradient id="ipdBarG" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#1a4a7a" /><stop offset="100%" stopColor="#0d233f" />
            </linearGradient>
          </defs>
          {data.map((item, idx) => {
            const bw   = 32;
            const sp   = (100 - 10) / data.length;
            const xPos = `${10 + idx * sp}%`;
            const bh   = (item.amount / max) * (H - 55);
            const yPos = H - 40 - bh;
            return (
              <g key={idx} style={{ cursor: "pointer" }}>
                <rect x={xPos} y={yPos} width={`${bw}px`} height={`${Math.max(bh, 4)}px`} rx="5"
                  fill={hovered === idx ? "#1a4a7a" : "url(#ipdBarG)"}
                  onMouseEnter={() => setHovered(idx)} onMouseLeave={() => setHovered(null)}
                  style={{ transition: "fill 0.15s" }} />
                <text x={xPos} dx={bw / 2} y={H - 14} textAnchor="middle" fill={C.textSub} style={{ fontSize: "11px", fontWeight: "600" }}>{item.day}</text>
              </g>
            );
          })}
        </svg>
      </div>
      )}
    </div>
  );
}

function DeptRevenueCard({ data }) {
  const barColors = ["#0d233f", "#1a4a7a", "#2563a8", "#94a3b8", "#94a3b8", "#94a3b8", "#94a3b8"];
  const fmt = n => new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(n);
  return (
    <div style={{ background: C.card, borderRadius: "10px", padding: "20px", border: `1px solid ${C.border}`, flex: "1 1 40%", boxShadow: "0 2px 8px rgba(13,35,63,0.07)" }}>
      <h3 style={{ margin: "0 0 2px", fontSize: "15px", fontWeight: "700", color: C.text }}>Revenue by Department</h3>
      <span style={{ fontSize: "12px", color: C.textSub }}>Gross billing contribution</span>
      <div style={{ display: "flex", flexDirection: "column", gap: "12px", marginTop: "16px", maxHeight: "220px", overflowY: "auto" }}>
        {data.map((dept, i) => (
          <div key={i}>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "5px" }}>
              <span style={{ fontSize: "13px", fontWeight: "600", color: C.text }}>{dept.name}</span>
              <span style={{ fontSize: "12px", fontWeight: "700", color: C.text }}>{fmt(dept.amount)}</span>
            </div>
            <div style={{ height: "6px", background: "#f1f3f6", borderRadius: "100px", overflow: "hidden" }}>
              <div style={{ width: `${dept.pct}%`, height: "100%", background: barColors[i] || "#94a3b8", borderRadius: "100px", transition: "width 0.8s ease" }} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

const RISK_STYLE = {
  High:   { background: "#fdecea", color: "#842029", border: "1px solid #f5c2c7" },
  Medium: { background: "#fff8e1", color: "#856404", border: "1px solid #ffe69c" },
  Low:    { background: "#d1f2db", color: "#145a32", border: "1px solid #badbcc" },
};

const OUTSTANDING_COLUMNS = [
  { field: "name", headerName: "PATIENT", flex: 1.8, minWidth: 220, sortable: false,
    renderCell: (p) => (
      <Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
        <Box sx={{ width: "34px", height: "34px", borderRadius: "50%", background: C.accent, color: C.primary, display: "flex", alignItems: "center", justifyContent: "center", fontSize: "12px", fontWeight: 700, flexShrink: 0 }}>{p.row.initials}</Box>
        <Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: "5px" }}>
            <Box component="span" sx={{ fontSize: "13px", fontWeight: 700, color: C.text }}>{p.row.name}</Box>
            {p.row.abha && <Box component="span" sx={{ fontSize: "9px", fontWeight: 700, padding: "1px 5px", borderRadius: "100px", background: "#d1f2db", color: "#145a32" }}>ABHA</Box>}
          </Box>
          <Box sx={{ fontSize: "11px", color: C.textSub, lineHeight: 1.4 }}>{p.row.uhid ? `UHID: ${p.row.uhid}` : ""}{p.row.age ? ` · ${p.row.age}` : ""}</Box>
        </Box>
      </Box>
    ) },
  { field: "ward", headerName: "WARD / BED", flex: 1, minWidth: 130, sortable: false,
    renderCell: (p) => <Box component="span" sx={{ fontSize: "13px", color: C.textSub }}>{p.value}</Box> },
  { field: "payer", headerName: "PAYER", flex: 1, minWidth: 120, sortable: false,
    renderCell: (p) => <Box component="span" sx={{ fontSize: "13px", fontWeight: 700, color: C.text }}>{p.value}</Box> },
  { field: "los", headerName: "LOS", flex: 0.5, minWidth: 70, sortable: false,
    renderCell: (p) => <Box component="span" sx={{ fontSize: "13px", fontWeight: 600, color: C.text }}>{p.value}</Box> },
  { field: "billed", headerName: "BILLED", flex: 0.9, minWidth: 110, sortable: false,
    renderCell: (p) => <Box component="span" sx={{ fontSize: "13px", fontWeight: 600, color: C.text }}>{fmtINR(p.value)}</Box> },
  { field: "deposit", headerName: "DEPOSIT", flex: 0.9, minWidth: 110, sortable: false,
    renderCell: (p) => <Box component="span" sx={{ fontSize: "13px", color: C.textSub }}>{fmtINR(p.value)}</Box> },
  { field: "balance", headerName: "BALANCE", flex: 0.9, minWidth: 110, sortable: false,
    renderCell: (p) => (
      <Box component="span" sx={{ fontSize: "14px", fontWeight: 700, color: p.value > 100000 ? C.danger : p.value < 0 ? C.success : C.text }}>
        {fmtINR(p.value)}
      </Box>
    ) },
  { field: "risk", headerName: "RISK", flex: 0.7, minWidth: 100, sortable: false,
    renderCell: (p) => (
      <Box component="span" sx={{ fontSize: "11px", fontWeight: 700, padding: "4px 10px", borderRadius: "100px", ...(RISK_STYLE[p.value] || {}) }}>
        {p.value === "High" ? "⚠ " : ""}{p.value}
      </Box>
    ) },
];

function OutstandingTable({ data }) {
  const [search, setSearch] = useState("");
  const filtered = data.filter(r =>
    r.name.toLowerCase().includes(search.toLowerCase()) ||
    r.payer.toLowerCase().includes(search.toLowerCase()) ||
    r.uhid.toLowerCase().includes(search.toLowerCase())
  );
  return (
    <div style={{ background: C.card, borderRadius: "10px", padding: "20px", border: `1px solid ${C.border}`, boxShadow: "0 2px 8px rgba(13,35,63,0.07)" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "12px", marginBottom: "18px" }}>
        <div>
          <h3 style={{ margin: "0 0 2px", fontSize: "15px", fontWeight: "700", color: C.text }}>High Outstanding Accounts</h3>
          <span style={{ fontSize: "12px", color: C.textSub }}>IPD patients with significant balances needing immediate billing action</span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <IpdTextField
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search patient / payer…"
            sx={{ width: "230px" }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SvgIcon name="search" size={15} color={C.textSub} />
                </InputAdornment>
              ),
            }}
          />
        </div>
      </div>
      <IpdDataGrid
        rows={filtered}
        columns={OUTSTANDING_COLUMNS}
        rowHeight={60}
        embedded
        emptyMessage="No high-outstanding accounts."
      />
    </div>
  );
}

function Skeleton({ w = "100%", h = "16px", r = "6px" }) {
  return <div style={{ width: w, height: h, borderRadius: r, background: "linear-gradient(90deg,#e9ecef 25%,#f8f9fa 50%,#e9ecef 75%)", backgroundSize: "200% 100%", animation: "ipdShimmer 1.4s infinite" }} />;
}

// ─────────────────────────────────────────────────────────────────────────────
// MAIN
// ─────────────────────────────────────────────────────────────────────────────
export default function IPDBillingDashboard() {
  const facilityName = sessionStorage.getItem("facility_name") || "Your Facility";

  const [isLoading,   setIsLoading]   = useState(true);
  const [error,       setError]       = useState("");
  const [kpis,        setKpis]        = useState([]);
  const [collections, setCollections] = useState([]);
  const [wowPercent,  setWowPercent]  = useState(null);
  // const [deptRevenue] = useState(MOCK_DEPT_REVENUE);
  const [outstanding, setOutstanding] = useState([]);

  const todayLabel = new Date().toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });

  useEffect(() => {
    let cancelled = false;
    const fetchDashboardData = async () => {
      setIsLoading(true);
      setError("");
      try {
        const [summary, accounts] = await Promise.all([
          getBillingDashboardSummary(),
          getHighOutstandingAccounts(20, 0).catch(() => []),
        ]);
        if (cancelled) return;
        setKpis(buildKpis(summary || {}));
        setCollections(mapCollections(summary?.daily_collections_week));
        setWowPercent(summary?.daily_collections_wow_percent ?? null);
        setOutstanding((accounts || []).map(mapOutstandingRow));
      } catch (err) {
        if (cancelled) return;
        console.error("IPD Dashboard fetch error:", err);
        setError(err.message || "Failed to load dashboard");
        setKpis(buildKpis({}));
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };
    fetchDashboardData();
    return () => { cancelled = true; };
  }, []);

  return (
    <IPDBillingLayout activeNav="dashboard">
      <div style={{ padding: "28px", display: "flex", flexDirection: "column", gap: "20px" }}>

        {/* Title + actions */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", flexWrap: "wrap", gap: "14px" }}>
          <div>
            <h2 style={{ margin: "0 0 4px", fontSize: "22px", fontWeight: "800", color: C.text }}>IPD Billing Command Center</h2>
            <p style={{ margin: 0, fontSize: "13px", color: C.textSub }}>{facilityName} · {todayLabel}</p>
            {error && <p style={{ margin: "6px 0 0", fontSize: "12px", color: C.danger, fontWeight: "600" }}>{error}</p>}
          </div>
          <div style={{ display: "flex", gap: "10px" }}>
            {["filter", "export"].map(icon => (
              <IpdButton key={icon} variant="outlined" startIcon={<SvgIcon name={icon} size={15} color={C.textSub} />}>
                {icon.charAt(0).toUpperCase() + icon.slice(1)}
              </IpdButton>
            ))}
            {/* <button
              style={{ background: C.primary, color: "#fff", border: "none", borderRadius: "7px", padding: "9px 18px", fontSize: "13px", fontWeight: "700", cursor: "pointer", display: "inline-flex", alignItems: "center", gap: "7px", boxShadow: "0 2px 8px rgba(13,35,63,0.2)", transition: "background 0.15s" }}
              onMouseEnter={e => e.currentTarget.style.background = C.primaryHover}
              onMouseLeave={e => e.currentTarget.style.background = C.primary}
            >
              <SvgIcon name="add" size={15} color="#fff" /> New Admission Billing
            </button> */}
          </div>
        </div>

        {/* KPI Cards */}
        <div style={{ display: "flex", flexWrap: "wrap", gap: "14px" }}>
          {isLoading
            ? Array(6).fill(0).map((_, i) => (
                <div key={i} style={{ flex: "1 1 calc(16.66% - 14px)", minWidth: "160px", background: C.card, borderRadius: "10px", padding: "18px 20px", border: `1px solid ${C.border}`, display: "flex", flexDirection: "column", gap: "12px" }}>
                  <Skeleton w="60%" h="13px" /><Skeleton w="40%" h="26px" /><Skeleton w="80%" h="13px" />
                </div>
              ))
            : kpis.map(k => <KPICard key={k.id} data={k} />)
          }
        </div>

        {/* Charts */}
        <div style={{ display: "flex", gap: "20px", flexWrap: "wrap" }}>
          {isLoading
            ? <div style={{ flex: 1, background: C.card, borderRadius: "10px", padding: "20px", border: `1px solid ${C.border}`, minHeight: "280px" }}><Skeleton w="40%" h="16px" /><div style={{ marginTop: "12px" }}><Skeleton w="100%" h="180px" r="8px" /></div></div>
            : <CollectionChart data={collections} wowPercent={wowPercent} />
          }
          {/* {isLoading
            ? <div style={{ flex: "0 1 38%", background: C.card, borderRadius: "10px", padding: "20px", border: `1px solid ${C.border}`, minHeight: "280px" }}><Skeleton w="50%" h="16px" /><div style={{ display: "flex", flexDirection: "column", gap: "14px", marginTop: "16px" }}>{Array(5).fill(0).map((_, i) => <Skeleton key={i} h="28px" />)}</div></div>
            : <DeptRevenueCard data={deptRevenue} />
          } */}
        </div>

        {/* Outstanding Table */}
        {isLoading
          ? <div style={{ background: C.card, borderRadius: "10px", padding: "20px", border: `1px solid ${C.border}` }}><Skeleton w="30%" h="16px" /><div style={{ marginTop: "16px", display: "flex", flexDirection: "column", gap: "10px" }}>{Array(4).fill(0).map((_, i) => <Skeleton key={i} h="44px" />)}</div></div>
          : <OutstandingTable data={outstanding} />
        }

      </div>
    </IPDBillingLayout>
  );
}
